import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAdminAuth, API } from '../context/AdminAuthContext';

/**
 * useAdminPermissions — single source of truth for the current admin's
 * granted permission set. Loaded once per session from `/api/admin/me`.
 *
 * The hook exposes `can(slug)` mirroring the backend's `_perm_set_grants`
 * rules:
 *   - `*` wildcard / super-admin → always true.
 *   - Exact match → true.
 *   - Bare module slug (`products`) grants any `{module}.{action}`.
 *   - A `{module}.{action}` slug satisfies bare-module checks (menu gating).
 *
 * UI MUST treat backend 403s as the canonical authority. This hook only
 * exists to hide controls the user clearly can't use; it isn't a
 * security boundary.
 */
export const useAdminPermissions = () => {
  const { token, authHeaders } = useAdminAuth();
  const [me, setMe] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!token) {
      setMe(null);
      setLoaded(true);
      return;
    }
    let alive = true;
    axios
      .get(`${API}/admin/me`, { headers: authHeaders })
      .then(({ data }) => {
        if (!alive) return;
        setMe(data);
        setLoaded(true);
      })
      .catch(() => {
        if (!alive) return;
        setMe({ permissions: [], is_super_admin: false });
        setLoaded(true);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const perms = me?.permissions || [];
  const isSuper = !!me?.is_super_admin;

  const can = (slug) => {
    if (!slug) return false;
    if (isSuper || perms.includes('*')) return true;
    if (perms.includes(slug)) return true;
    if (slug.includes('.')) {
      const mod = slug.split('.', 1)[0];
      if (perms.includes(mod)) return true;
    } else {
      const prefix = `${slug}.`;
      if (perms.some((p) => p.startsWith(prefix))) return true;
    }
    return false;
  };

  return { me, loaded, isSuper, perms, can };
};

export default useAdminPermissions;
